import { Language } from '../types/cms.types'
import {
    HeroContent,
    IntroContent,
    MenuContent,
    MenuDetailContent,
    GalleryContent,
    ReviewsContent,
    FooterContent,
    VotedKeywordsContent,
    PressContent
} from '../types/content.types'

class CMSService {
    private contentCache: Map<string, any> = new Map()
    private contentSource: 'local' | 'tigris'
    private tigrisBucketUrl: string

    constructor() {
        // 환경변수에서 설정 가져오기
        this.contentSource = (import.meta.env.VITE_CONTENT_SOURCE || 'local') as 'local' | 'tigris'
        this.tigrisBucketUrl = import.meta.env.VITE_TIGRIS_BUCKET_URL || 'https://fly.storage.tigris.dev/blackpork1931'

        console.log(`📦 CMS Service initialized - Source: ${this.contentSource}`)
        if (this.contentSource === 'tigris') {
            console.log(`🌐 Tigris URL: ${this.tigrisBucketUrl}`)
        }
    }

    private getCacheKey(componentType: string, language: Language): string {
        return `${componentType}_${language}`
    }

    private async loadFromLocal<T>(componentType: string, language: Language, optional = false): Promise<T> {
        try {
            // Dynamic import based on language and component type
            // Note: Local mode uses cms-content/data folder
            const content = await import(`../../cms-content/data/${language}/${componentType}.json`)
            return content.default as T
        } catch (error) {
            // optional 콘텐츠(예: 영어 press)는 부재가 정상이므로 에러 로그를 남기지 않음
            if (!optional) {
                console.error(`Failed to load local content for ${componentType} in ${language}:`, error)
            }
            throw new Error(`Local content not found: ${componentType} (${language})`)
        }
    }

    private async loadFromTigris<T>(componentType: string, language: Language, optional = false): Promise<T> {
        try {
            const url = `${this.tigrisBucketUrl}/content/${language}/${componentType}.json`
            console.log(`🌐 Fetching from Tigris: ${url}`)

            const response = await fetch(url)

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`)
            }

            const content = await response.json()
            return content as T
        } catch (error) {
            // optional 콘텐츠는 부재가 정상 → 에러 로그 억제
            if (!optional) {
                console.error(`Failed to load Tigris content for ${componentType} in ${language}:`, error)
            }
            throw new Error(`Tigris content not found: ${componentType} (${language})`)
        }
    }

    async loadContent<T>(componentType: string, language: Language, optional = false): Promise<T> {
        const cacheKey = this.getCacheKey(componentType, language)

        // Check cache first
        if (this.contentCache.has(cacheKey)) {
            return this.contentCache.get(cacheKey)
        }

        let content: T

        if (this.contentSource === 'tigris') {
            try {
                content = await this.loadFromTigris<T>(componentType, language, optional)
            } catch (tigrisError) {
                if (!optional) {
                    console.warn(
                        `⚠️ Tigris fetch failed for ${componentType} (${language}). Falling back to local.`,
                        tigrisError
                    )
                }
                try {
                    content = await this.loadFromLocal<T>(componentType, language, optional)
                } catch (localError) {
                    if (!optional) {
                        console.error(
                            `Failed to load content for ${componentType} in ${language} from both sources.`,
                            { tigrisError, localError }
                        )
                    }
                    throw localError
                }
            }
        } else {
            try {
                content = await this.loadFromLocal<T>(componentType, language, optional)
            } catch (error) {
                if (!optional) {
                    console.error(`Failed to load content for ${componentType} in ${language}:`, error)
                }
                throw error
            }
        }

        this.contentCache.set(cacheKey, content)
        return content
    }

    async loadHeroContent(language: Language): Promise<HeroContent> {
        return this.loadContent<HeroContent>('hero', language)
    }

    async loadIntroContent(language: Language): Promise<IntroContent> {
        return this.loadContent<IntroContent>('intro', language)
    }

    async loadMenuContent(language: Language): Promise<MenuContent> {
        return this.loadContent<MenuContent>('menu', language)
    }

    async loadMenuDetailContent(language: Language): Promise<MenuDetailContent> {
        return this.loadContent<MenuDetailContent>('menuDetail', language)
    }

    async loadGalleryContent(language: Language): Promise<GalleryContent> {
        return this.loadContent<GalleryContent>('gallery', language)
    }

    async loadReviewsContent(language: Language): Promise<ReviewsContent> {
        return this.loadContent<ReviewsContent>('reviews', language)
    }

    async loadFooterContent(language: Language): Promise<FooterContent> {
        return this.loadContent<FooterContent>('footer', language)
    }

    async loadVotedKeywordsContent(language: Language): Promise<VotedKeywordsContent | null> {
        try {
            return await this.loadContent<VotedKeywordsContent>('votedKeywords', language, true)
        } catch {
            return null // 아직 데이터 없으면 섹션 숨김
        }
    }

    async loadPressContent(language: Language): Promise<PressContent | null> {
        try {
            return await this.loadContent<PressContent>('press', language, true)
        } catch {
            return null // 해당 언어에 press 데이터 없으면 섹션 숨김 (예: 영어)
        }
    }


    clearCache(): void {
        this.contentCache.clear()
    }

    clearCacheForLanguage(language: Language): void {
        const keysToDelete: string[] = []

        this.contentCache.forEach((_, key) => {
            if (key.endsWith(`_${language}`)) {
                keysToDelete.push(key)
            }
        })

        keysToDelete.forEach(key => this.contentCache.delete(key))
    }
}

// Singleton instance
export const cmsService = new CMSService()
export default cmsService
